import React, { useState, useEffect, useRef, useCallback } from 'react'
import '../../styles/CreateTicketPage.css'
import {
  LANGUAGES, EXPERIENCES, PRIORITIES, CHANNELS, SOURCE_CHANNELS,
  CUSTOMER_TYPES, TICKET_TYPES, LOCATION_TYPES,
} from '../../constants/ticketFormOptions'

// Normalize /api/vehicles/search-bar response to always be an array of
// { customer_details, vehicle_details } objects
const normalizeVehicleResults = (data) => {
  if (!data) return []
  const hasContent = r => {
    const vd = r?.vehicle_details
    const cd = r?.customer_details
    return (vd && (vd.vin_number || vd.vin || vd.registration_number || vd.id)) ||
      (cd && (cd.name || cd.phone))
  }
  if (hasContent(data)) return [data]
  if (Array.isArray(data)) return data.filter(hasContent)
  if (data.results) {
    const r = data.results
    if (Array.isArray(r)) return r.filter(hasContent)
    if (hasContent(r)) return [r]
  }
  return []
}

const emptyForm = () => ({
  // Vehicle
  vin: '', registrationNumber: '', vehicleId: '',
  batteryId: '', qisId: '', vehicleType: '', oemBrandName: '',
  serviceProvider: '', cncErrorCode: '', cncTransactionId: '',
  // Customer
  riderName: '', phone: '', email: '',
  preferredChannel: '', customerType: '', channel: '',
  language: 'Hindi', experience: '',
  // Issue
  categoryName: '', subCategoryName: '', categoryId: '', subCategoryId: '',
  categorySlug: '', subCategorySlug: '',
  region: '', coordinatorId: '', coordinatorName: '', changeCoordinatorId: '',
  ticketType: 'call_center', priority: 'urgent', previousTicketId: '', disposition: '',
  locationType: '',
  stationId: '', stationSlug: '',           // stationId=UUID for payload, stationSlug for dependent lookups
  nearbyStationId: '', dockId: '',
  state: '', stateId: '',                   // stateId=UUID for payload
  city: '', citySlug: '',                   // city=UUID for payload, citySlug for stations API
  regionId: '', regionSlug: '',             // regionId=UUID for coordinator API, regionSlug if needed
  area: '', description: '',
})

const CreateTicketPage = ({ fetchJson, setCurrentPage, setSelectedVehicle, setSelectedTicket, callLogId: callLogIdProp = '', fromCallProp = false }) => {
  const [form, setForm] = useState(emptyForm())
  const [draftModalOpen, setDraftModalOpen] = useState(false)
  const draftSaveTimer = useRef(null)
  const latestDraftRef = useRef({ form: emptyForm() })
  const ticketCreatedRef = useRef(false)
  const [submitting, setSubmitting] = useState(false)
  const [resolvingNow, setResolvingNow] = useState(false)
  const [createdTicket, setCreatedTicket] = useState(null)

  /* ── toasts ── */
  const [toasts, setToasts] = useState([])
  const toastTimers = useRef({})
  useEffect(() => () => { Object.values(toastTimers.current).forEach(clearTimeout) }, [])

  const dismissToast = (id) => {
    clearTimeout(toastTimers.current[id])
    delete toastTimers.current[id]
    setToasts(t => t.filter(x => x.id !== id))
  }

  const showToast = (ok, msg, withViewTickets = false) => {
    const id = Date.now()
    setToasts(t => [...t, { id, ok, msg, withViewTickets }])
    toastTimers.current[id] = setTimeout(() => dismissToast(id), 5000)
  }

  /* prop takes priority; fall back to localStorage for the new-tab auto-open scenario */
  const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : new URLSearchParams()
  const callLogId = callLogIdProp || (typeof window !== 'undefined' ? localStorage.getItem('ozonetel_call_log_id') || '' : '')
  const fromCall = fromCallProp || urlParams.get('source') === 'call'   // true when auto-opened by busyAgent
  const isCallTab = urlParams.get('nodialer') === '1'                   // true only in the auto-opened new tab

  /* guard so the auto-search only fires once on mount */
  const autoSearchedRef = useRef(false)

  const DRAFT_KEY = 'crm_ticket_draft'

  /* ── check for existing draft on mount ── */
  useEffect(() => {
    if (fromCall) return
    try {
      const raw = localStorage.getItem(DRAFT_KEY)
      if (raw) {
        const draft = JSON.parse(raw)
        if (draft?.form && Object.values(draft.form).some(v => v !== '' && v !== null && v !== undefined)) {
          setDraftModalOpen(true)
        }
      }
    } catch { }
  }, [])

  /* ── keep ref in sync so unmount handler always has latest values ── */
  useEffect(() => {
    latestDraftRef.current = { form, fromCall }
  })

  /* ── save draft to localStorage on form change (debounced) ── */
  useEffect(() => {
    if (fromCall) return
    clearTimeout(draftSaveTimer.current)
    draftSaveTimer.current = setTimeout(() => {
      const hasData = form.vin || form.riderName || form.phone || form.categorySlug || form.description
      if (hasData) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form }))
      }
    }, 500)
    return () => clearTimeout(draftSaveTimer.current)
  }, [form, fromCall])

  /* ── save draft immediately on unmount (catches quick navigations) ── */
  useEffect(() => {
    return () => {
      const { form: f, fromCall: fc } = latestDraftRef.current
      if (fc) return
      const hasData = f.vin || f.riderName || f.phone || f.categorySlug || f.description
      if (hasData) {
        localStorage.setItem(DRAFT_KEY, JSON.stringify({ form: f }))
      }
    }
  }, [])

  const handleRestoreDraft = () => {
    try {
      const draft = JSON.parse(localStorage.getItem(DRAFT_KEY) || '{}')
      if (draft?.form) {
        setForm(draft.form)
      }
    } catch { }
    setDraftModalOpen(false)
  }

  const handleDiscardDraft = () => {
    localStorage.removeItem(DRAFT_KEY)
    setDraftModalOpen(false)
    handleReset()
  }

  /* ── browser tab close / refresh guard ── */
  useEffect(() => {
    if (!fromCall) return
    const handleBeforeUnload = (e) => {
      if (ticketCreatedRef.current) return
      e.preventDefault()
      e.returnValue = ''
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [fromCall])

  /* ── categories ── */
  const [catList, setCatList] = useState([])
  const [catsLoading, setCatsLoading] = useState(false)
  const [catsError, setCatsError] = useState(null)
  const [catPage, setCatPage] = useState(1)
  const [catHasMore, setCatHasMore] = useState(false)
  const [catOpen, setCatOpen] = useState(false)
  const [catSelectedName, setCatSelectedName] = useState('')
  const catDropRef = useRef(null)
  const catListRef = useRef(null)
  const catLoadingRef = useRef(false)
  const [subcatList, setSubcatList] = useState([])
  const [subcatsLoading, setSubcatsLoading] = useState(false)
  const [subcatPage, setSubcatPage] = useState(1)
  const [subcatHasMore, setSubcatHasMore] = useState(false)
  const [subcatOpen, setSubcatOpen] = useState(false)
  const [subcatSelectedName, setSubcatSelectedName] = useState('')
  const subcatDropRef = useRef(null)
  const subcatListRef = useRef(null)
  const subcatLoadingRef = useRef(false)

  /* ── vehicle lookup (auto mode) ── */
  const [vSearch, setVSearch] = useState('')
  const [vResults, setVResults] = useState([])
  const [vSearching, setVSearching] = useState(false)
  const [vLastSearch, setVLastSearch] = useState('')
  const [vehicleFound, setVehicleFound] = useState(null)
  const [showAllTickets, setShowAllTickets] = useState(false)
  const vDebounce = useRef(null)
  const vDropRef = useRef(null)

  /* ── customer search ── */
  const [cSearch, setCSearch] = useState('')
  const [cResults, setCResults] = useState([])
  const [cSearching, setCSearching] = useState(false)
  const [cLastSearch, setCLastSearch] = useState('')
  const cDebounce = useRef(null)
  const cDropRef = useRef(null)

  const loadCategories = useCallback(async (page = 1, append = false) => {
    if (!fetchJson || catLoadingRef.current) return
    catLoadingRef.current = true
    setCatsLoading(true)
    if (!append) setCatsError(null)
    try {
      const params = new URLSearchParams({ page: String(page) })
      const data = await fetchJson(`/api/categories/category?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setCatList(prev => append ? [...prev, ...results] : results)
      setCatHasMore(!!data?.next)
      setCatPage(page)
    } catch (err) {
      setCatsError(err?.message || 'Failed to load categories')
    } finally {
      catLoadingRef.current = false
      setCatsLoading(false)
    }
  }, [fetchJson])

  useEffect(() => { loadCategories(1, false) }, [loadCategories])

  const handleCatScroll = useCallback(() => {
    const el = catListRef.current
    if (!el || !catHasMore || catsLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadCategories(catPage + 1, true)
    }
  }, [catHasMore, catsLoading, catPage, loadCategories])

  useEffect(() => {
    if (!catOpen) return
    const handler = (e) => { if (catDropRef.current && !catDropRef.current.contains(e.target)) setCatOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [catOpen])

  /* ── coordinator auto-fetch ── */
  const [coordinatorLoading, setCoordinatorLoading] = useState(false)
  useEffect(() => {
    if (!fetchJson || !form.categorySlug || !form.subCategorySlug || !form.customerType) return
    setCoordinatorLoading(true)
    const params = new URLSearchParams({
      category: form.categorySlug,
      sub_category: form.subCategorySlug,
      customer_type: form.customerType,
      ...(form.regionId ? { region: form.regionId } : {}),
    })
    fetchJson(`/api/categories/get-coordinator?${params}`)
      .then(res => {
        const coord = res?.coordinator || res
        const name = coord?.name || coord?.coordinator_name || ''
        const id = coord?.id || coord?.coordinator_id || ''
        setForm(f => ({ ...f, coordinatorId: id, coordinatorName: name }))
      })
      .catch((err) => {
        if (err?.status === 400 || err?.status === 404) {
          setForm(f => ({ ...f, coordinatorId: '', coordinatorName: '' }))
        }
      })
      .finally(() => setCoordinatorLoading(false))
  }, [form.categorySlug, form.subCategorySlug, form.customerType, form.regionId, fetchJson])

  /* ── fetch previous tickets when follow_up is selected and phone is available ── */
  useEffect(() => {
    if (form.ticketType !== 'follow_up' || !form.phone || !fetchJson) {
      setPrevTicketsList([])
      set('previousTicketId', '')
      return
    }
    setPrevTicketsLoading(true)
    fetchJson(`/api/tickets/ticket/customer-tickets?phone=${encodeURIComponent(form.phone)}&count=2`)
      .then(res => setPrevTicketsList(res?.tickets || []))
      .catch(() => setPrevTicketsList([]))
      .finally(() => setPrevTicketsLoading(false))
  }, [form.ticketType, form.phone, fetchJson])

  /* ── fetch OEM / Service Provider / CNC Error Code lists once on mount ── */
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/vehicles/oem').then(data => setOemList(Array.isArray(data) ? data : (data?.results || []))).catch(() => { })
    fetchJson('/api/vehicles/service-provider').then(data => setServiceProviderList(Array.isArray(data) ? data : (data?.results || []))).catch(() => { })
    fetchJson('/api/vehicles/cnc-error').then(data => setCncErrorList(Array.isArray(data) ? data : (data?.results || []))).catch(() => { })
    setDispositionLoading(true)
    fetchJson('/api/categories/disposition').then(data => setDispositionList(Array.isArray(data) ? data : (data?.results || []))).catch(() => { }).finally(() => setDispositionLoading(false))
  }, [fetchJson])

  /* ── operators for "Change Coordinator" searchable dropdown ── */
  const [opSearch, setOpSearch] = useState('')
  const [opList, setOpList] = useState([])
  const [opPage, setOpPage] = useState(1)
  const [opHasMore, setOpHasMore] = useState(false)
  const [opLoading, setOpLoading] = useState(false)
  const [opOpen, setOpOpen] = useState(false)
  const [opSelectedName, setOpSelectedName] = useState('')
  const opDropRef = useRef(null)
  const opListRef = useRef(null)
  const opSearchDebounce = useRef(null)
  const opLoadingRef = useRef(false)

  /* ── previous tickets (follow-up type) ── */
  const [prevTicketsList, setPrevTicketsList] = useState([])
  const [prevTicketsLoading, setPrevTicketsLoading] = useState(false)

  /* ── OEM / Service Provider / CNC Error Code — fetched from API ── */
  const [oemList, setOemList] = useState([])
  const [serviceProviderList, setServiceProviderList] = useState([])
  const [cncErrorList, setCncErrorList] = useState([])
  const [dispositionList, setDispositionList] = useState([])
  const [dispositionLoading, setDispositionLoading] = useState(false)

  /* ── station searchable dropdown ── */
  const [stationOpen, setStationOpen] = useState(false)
  const [stationSearch, setStationSearch] = useState('')
  const [stationSelectedName, setStationSelectedName] = useState('')
  const stationDropRef = useRef(null)
  const stationListRef = useRef(null)
  const stationsLoadingRef = useRef(false)
  const stationSearchDebounce = useRef(null)

  const loadOperators = useCallback(async (page, search, append = false) => {
    if (!fetchJson || opLoadingRef.current) return
    opLoadingRef.current = true
    setOpLoading(true)
    try {
      const params = new URLSearchParams({ agent_mode: 'coordinator', page: String(page) })
      if (search) params.set('q', search)
      const data = await fetchJson(`/api/agents/agent/?${params}`)
      const results = data?.results || []
      setOpList(prev => append ? [...prev, ...results] : results)
      setOpHasMore(!!data?.next)
      setOpPage(page)
    } catch { }
    finally { opLoadingRef.current = false; setOpLoading(false) }
  }, [fetchJson])

  useEffect(() => {
    if (!opOpen) return
    clearTimeout(opSearchDebounce.current)
    if (opSearch.length === 1) return
    opSearchDebounce.current = setTimeout(() => loadOperators(1, opSearch, false), opSearch.length >= 2 ? 300 : 0)
    return () => clearTimeout(opSearchDebounce.current)
  }, [opOpen, opSearch, loadOperators])

  useEffect(() => {
    if (!opOpen) return
    const handler = (e) => { if (opDropRef.current && !opDropRef.current.contains(e.target)) setOpOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [opOpen])

  const handleOpScroll = useCallback(() => {
    const el = opListRef.current
    if (!el || !opHasMore || opLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadOperators(opPage + 1, opSearch, true)
    }
  }, [opHasMore, opLoading, opPage, opSearch, loadOperators])

  /* ── attachments ── */
  const [attachments, setAttachments] = useState([])
  const fileInputRef = useRef(null)

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    if (files.length) setAttachments(prev => [...prev, ...files])
    e.target.value = ''
  }

  const removeAttachment = (index) => setAttachments(prev => prev.filter((_, i) => i !== index))

  /* ── regions list — fetched on mount ── */
  const [regionsList, setRegionsList] = useState([])
  const [regionsLoading, setRegionsLoading] = useState(true)
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/geographies/geography/regions')
      .then(data => setRegionsList(Array.isArray(data) ? data : (data?.data || data?.results || [])))
      .catch(() => { })
      .finally(() => setRegionsLoading(false))
  }, [fetchJson])

  /* ── cities list — fetched on mount ── */
  const [citiesList, setCitiesList] = useState([])
  const [citiesLoading, setCitiesLoading] = useState(true)
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/geographies/geography/cities')
      .then(data => {
        setCitiesList(Array.isArray(data) ? data : (data?.data || data?.results || []))
      })
      .catch(() => { })
      .finally(() => setCitiesLoading(false))
  }, [fetchJson])

  /* ── states list — fetched on mount ── */
  const [statesList, setStatesList] = useState([])
  const [statesLoading, setStatesLoading] = useState(true)
  useEffect(() => {
    if (!fetchJson) return
    fetchJson('/api/geographies/geography/states')
      .then(data => setStatesList(Array.isArray(data) ? data : (data?.data || data?.results || [])))
      .catch(() => { })
      .finally(() => setStatesLoading(false))
  }, [fetchJson])

  /* ── stations list — paginated with infinite scroll ── */
  const [stationsList, setStationsList] = useState([])
  const [stationsLoading, setStationsLoading] = useState(false)
  const [stationsPage, setStationsPage] = useState(1)
  const [stationsHasMore, setStationsHasMore] = useState(false)

  const loadStations = useCallback(async (citySlug, page = 1, search = '', append = false) => {
    if (!fetchJson || !citySlug || stationsLoadingRef.current) return
    stationsLoadingRef.current = true
    setStationsLoading(true)
    try {
      const params = new URLSearchParams({ city: citySlug, page: String(page) })
      if (search) params.set('q', search)
      const data = await fetchJson(`/api/geographies/station?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setStationsList(prev => append ? [...prev, ...results] : results)
      setStationsHasMore(!!data?.next)
      setStationsPage(page)
    } catch {
      if (!append) setStationsList([])
    } finally {
      stationsLoadingRef.current = false
      setStationsLoading(false)
    }
  }, [fetchJson])

  useEffect(() => {
    if (!form.citySlug) { setStationsList([]); setStationsHasMore(false); setStationsPage(1); return }
    loadStations(form.citySlug, 1, '', false)
  }, [form.citySlug, loadStations])

  useEffect(() => {
    if (!stationOpen || !form.citySlug) return
    clearTimeout(stationSearchDebounce.current)
    if (stationSearch.length === 1) return
    stationSearchDebounce.current = setTimeout(() => loadStations(form.citySlug, 1, stationSearch, false), stationSearch.length >= 2 ? 300 : 0)
    return () => clearTimeout(stationSearchDebounce.current)
  }, [stationSearch, stationOpen, form.citySlug, loadStations])

  const handleStationScroll = useCallback(() => {
    const el = stationListRef.current
    if (!el || !stationsHasMore || stationsLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadStations(form.citySlug, stationsPage + 1, stationSearch, true)
    }
  }, [stationsHasMore, stationsLoading, stationsPage, stationSearch, form.citySlug, loadStations])

  /* close v/c dropdowns on outside click */
  useEffect(() => {
    function handler(e) {
      if (vDropRef.current && !vDropRef.current.contains(e.target)) { setVResults([]); setVLastSearch('') }
      if (cDropRef.current && !cDropRef.current.contains(e.target)) { setCResults([]); setCLastSearch('') }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  /* close station dropdown on outside click — same pattern as coordinator */
  useEffect(() => {
    if (!stationOpen) return
    const handler = (e) => {
      if (stationDropRef.current && !stationDropRef.current.contains(e.target)) setStationOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [stationOpen])

  /* reset station selection when city changes */
  useEffect(() => {
    setStationSelectedName('')
    setStationSearch('')
    setStationOpen(false)
  }, [form.city])

  /* ── helpers ── */
  const set = (field, value) => setForm(f => ({ ...f, [field]: value }))

  const loadSubCategories = useCallback(async (catSlug, page = 1, append = false) => {
    if (!fetchJson || !catSlug || subcatLoadingRef.current) return
    subcatLoadingRef.current = true
    setSubcatsLoading(true)
    try {
      const params = new URLSearchParams({ category: catSlug, page: String(page) })
      const data = await fetchJson(`/api/categories/category?${params}`)
      const results = Array.isArray(data) ? data : (data?.results || data?.data || [])
      setSubcatList(prev => append ? [...prev, ...results] : results)
      setSubcatHasMore(!!data?.next)
      setSubcatPage(page)
    } catch {
      if (!append) setSubcatList([])
    } finally {
      subcatLoadingRef.current = false
      setSubcatsLoading(false)
    }
  }, [fetchJson])

  const handleCategoryChange = (catSlug) => {
    const cat = catList.find(c => (c.slug || c.name) === catSlug)
    setForm(f => ({
      ...f,
      categorySlug: catSlug,
      categoryName: cat?.name || catSlug,
      categoryId: cat?.id || '',
      subCategorySlug: '', subCategoryName: '', subCategoryId: '',
    }))
    setSubcatList([])
    setSubcatHasMore(false)
    setSubcatPage(1)
    setSubcatSelectedName('')
    setSubcatOpen(false)
    if (catSlug) loadSubCategories(catSlug, 1, false)
  }

  const handleSubcatScroll = useCallback(() => {
    const el = subcatListRef.current
    if (!el || !subcatHasMore || subcatsLoading) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      loadSubCategories(form.categorySlug, subcatPage + 1, true)
    }
  }, [subcatHasMore, subcatsLoading, subcatPage, form.categorySlug, loadSubCategories])

  useEffect(() => {
    if (!subcatOpen) return
    const handler = (e) => { if (subcatDropRef.current && !subcatDropRef.current.contains(e.target)) setSubcatOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [subcatOpen])

  const handleSubCategoryChange = (subSlug, subName, subId) => {
    setForm(f => ({
      ...f,
      subCategorySlug: subSlug,
      subCategoryName: subName || subSlug,
      subCategoryId: subId || '',
    }))
  }

  /* ── pre-fill customer details from call data or restored previous ticket ── */
  useEffect(() => {
    const restorePrevious = localStorage.getItem('crm_restore_previous') === '1'
    if (!fromCall && !restorePrevious) return
    if (autoSearchedRef.current) return
    autoSearchedRef.current = true
    try {
      if (restorePrevious) localStorage.removeItem('crm_restore_previous')
      const callData = JSON.parse(localStorage.getItem('ozonetel_call_data') || '{}')
      const cd = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')?.customer_details || {}
      setForm(f => ({
        ...f,
        phone: cd.phone || callData.customerPhone || f.phone,
        riderName: cd.name || f.riderName,
        email: cd.email || f.email,
        customerType: cd.customer_type || f.customerType,
        experience: cd.experience || f.experience,
        language: cd.language || f.language,
        preferredChannel: cd.preferred_channel || f.preferredChannel,
        channel: cd.source_channel || (fromCall ? 'call' : f.channel),
      }))
    } catch { }
  }, [fromCall])

  /* ── vehicle search ── */
  const searchVehicles = useCallback((q) => {
    clearTimeout(vDebounce.current)
    if (!q.trim() || !fetchJson) { setVResults([]); setVLastSearch(''); return }
    vDebounce.current = setTimeout(() => {
      setVSearching(true)
      fetchJson(`/api/vehicles/vehicle/search?q=${encodeURIComponent(q)}`)
        .then(data => { setVLastSearch(q); setVResults(normalizeVehicleResults(data)) })
        .catch(() => { setVLastSearch(q); setVResults([]) })
        .finally(() => setVSearching(false))
    }, 300)
  }, [fetchJson])

  const selectVehicle = (v) => {
    setVehicleFound(v)
    setVResults([])
    setVLastSearch('')
    setVSearch('')
    const vd = v.vehicle_details || {}
    setForm(f => ({
      ...f,
      // clear all vehicle fields first, then fill from new selection
      vin: vd.vin_number || vd.vin || '',
      registrationNumber: vd.registration_number || '',
      vehicleId: vd.id ? String(vd.id) : '',
      batteryId: vd.battery_id || '',
      vehicleType: vd.vehicle_type || vd.type_of_vehicle || '',
      oemBrandName: vd.oem_brand_name || vd.oem || '',
      serviceProvider: vd.service_provider || '',
      cncTransactionId: vd.cnc_transaction_id || '',
      cncErrorCode: vd.cnc_error_code || '',
    }))
  }

  /* ── customer search ── */
  const searchCustomers = useCallback((q) => {
    clearTimeout(cDebounce.current)
    if (!q.trim() || !fetchJson) { setCResults([]); setCLastSearch(''); return }
    cDebounce.current = setTimeout(() => {
      setCSearching(true)
      fetchJson(`/api/customers/customer/search/?q=${encodeURIComponent(q)}`)
        .then(data => {
          setCLastSearch(q)
          const arr = Array.isArray(data) ? data : (data?.results ? data.results : (data ? [data] : []))
          setCResults(arr.filter(r => r?.customer_details?.name || r?.customer_details?.phone || r?.name || r?.phone))
        })
        .catch(() => { setCLastSearch(q); setCResults([]) })
        .finally(() => setCSearching(false))
    }, 300)
  }, [fetchJson])

  const selectCustomerResult = (r) => {
    setCResults([])
    setCSearch('')
    setCLastSearch('')
    const cd = r.customer_details || r
    const vd = r.vehicle_details || {}
    setForm(f => ({
      ...f,
      // clear all customer fields first, then fill from new selection
      riderName: cd.name || '',
      phone: cd.phone || '',
      email: cd.email || '',
      preferredChannel: cd.preferred_channel || '',
      channel: cd.source_channel || '',
      customerType: cd.customer_type || '',
      experience: cd.experience || '',
      language: cd.language || '',
    }))
  }

  /* ── submit ── */
  const handleSubmit = async (action = '') => {
    if (!form.vin.trim()) {
      showToast(false, 'Please enter Vehicle VIN No.')
      return
    }
    if (!form.registrationNumber.trim()) {
      showToast(false, 'Please enter Registration No.')
      return
    }
    if (!form.cncTransactionId.trim()) {
      showToast(false, 'Please enter CNC Transaction ID or mark it as N/A.')
      return
    }
    if (!form.channel) {
      showToast(false, 'Please select Source Channel.')
      return
    }
    if (!form.customerType) {
      showToast(false, 'Please select Customer Type.')
      return
    }
    if (!form.experience) {
      showToast(false, 'Please select Rider Experience.')
      return
    }
    if (!form.locationType) {
      showToast(false, 'Please select a Location Type.')
      return
    }
    if (!form.regionId) {
      showToast(false, 'Please select a Region.')
      return
    }
    if (!form.ticketType) {
      showToast(false, 'Please select Type of Ticket.')
      return
    }
    if (!form.categoryName || !form.subCategoryName) {
      showToast(false, 'Please select Issue Category and Sub-Category.')
      return
    }
    if (!form.description.trim()) {
      showToast(false, 'Please enter a description.')
      return
    }
    if (!form.city) {
      showToast(false, 'Please select an Incident City.')
      return
    }
    if (!form.stationId) {
      showToast(false, 'Please select a Station ID.')
      return
    }
    if (!fetchJson) { showToast(false, 'Not connected.'); return }

    if (action === 'resolve_now') setResolvingNow(true)
    else setSubmitting(true)

    let autoTicketId = null
    if (fromCall) {
      try {
        const autoTicket = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')
        autoTicketId = autoTicket?.id || autoTicket?.ticket_id || null
      } catch { }
    }

    try {
      const payload = {
        ticket_id: autoTicketId,
        vehicle_details: {
          vehicle_id: form.vehicleId || null,
          vin_number: form.vin || null,
          registration_number: form.registrationNumber || null,
          battery_id: form.batteryId || null,
          qis_id: form.qisId || null,
          type_of_vehicle: form.vehicleType || null,
          oem: form.oemBrandName || null,
          service_provider: form.serviceProvider || null,
          cnc_error_code: form.cncErrorCode || null,
          cnc_transaction_id: form.cncTransactionId || null,
        },
        customer_details: {
          name: form.riderName || null,
          email: form.email || null,
          phone: form.phone || null,
          preferred_channel: form.preferredChannel || null,
          customer_type: form.customerType || null,
          source_channel: form.channel || null,
          language: form.language || null,
          rider_experience: form.experience || null,
        },
        issue_details: {
          category: form.categorySlug || null,
          sub_category: form.subCategorySlug || null,
          changed_assigned_coordinator: form.changeCoordinatorId || null,
          ticket_type: form.ticketType || null,
          disposition: form.disposition || null,
          previous_ticket: form.previousTicketId || null,
          priority: form.priority || null,
          location_type: form.locationType || null,
          region: form.regionId || null,
          station: form.stationId || null,
          incident_state: form.stateId || null,
          incident_city: form.city || null,
          incident_area: form.area || null,
          dock_id: form.dockId || null,
          nearby_station: form.nearbyStationId || null,
          descripition: form.description || null,
        },
      }
      const endpoint = action
        ? `/api/tickets/ticket/create-ticket/?action=${action}`
        : '/api/tickets/ticket/create-ticket/'
      const res = await fetchJson(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })

      const newTicketId = res?.ticket_id || res?.id || null

      // Upload attachments if any — transparent to the user
      let attachmentWarning = ''
      if (attachments.length > 0 && newTicketId) {
        try {
          const formData = new FormData()
          attachments.forEach(file => formData.append('files', file))
          await fetchJson(
            `/api/tickets/ticket/add-attachments/${newTicketId}/?attachment_type=create_ticket`,
            { method: 'POST', body: formData },
          )
        } catch (attachErr) {
          attachmentWarning = ` (Attachments failed: ${attachErr?.message || 'upload error'})`
        }
      }

      const ticketForDetail = { ticket_id: newTicketId, ticket_number: res?.ticket_number || '' }
      setCreatedTicket(ticketForDetail)
      const ticketLabel = res?.ticket_number ? `#${res.ticket_number}` : ''
      showToast(!attachmentWarning, `Ticket ${ticketLabel} created successfully!${attachmentWarning}`, true)
      if (isCallTab) {
        setTimeout(() => {
          window.close()
        }, 1500)
      } else if (setSelectedTicket && setCurrentPage) {
        setTimeout(() => {
          setSelectedTicket(ticketForDetail)
          setCurrentPage('ticket-detail')
        }, 1000)
      }
      localStorage.removeItem(DRAFT_KEY)
      if (fromCall) {
        ticketCreatedRef.current = true
        localStorage.removeItem('ozonetel_auto_ticket')
        localStorage.removeItem('ozonetel_call_log_id')
        localStorage.removeItem('ozonetel_call_data')
      }
      setForm(emptyForm())
      setAttachments([])
      setVehicleFound(null); setShowAllTickets(false)
      setVSearch('')
    } catch (err) {
      const detail = err?.detail || err?.message || ''
      showToast(false, detail ? `Failed to create ticket: ${detail}` : 'Failed to create ticket. Please try again.')
    } finally {
      if (action === 'resolve_now') setResolvingNow(false)
      else setSubmitting(false)
    }
  }

  /* returns { phone, channel } pre-filled from call localStorage — used when resetting while fromCall */
  const getCallPreFill = () => {
    if (!fromCall) return {}
    try {
      const callData = JSON.parse(localStorage.getItem('ozonetel_call_data') || '{}')
      const ticketData = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')
      const phone = ticketData?.customer_details?.phone || callData.customerPhone || ''
      return { phone, channel: 'call' }
    } catch { return {} }
  }

  const handleReset = () => {
    setForm({ ...emptyForm(), ...getCallPreFill() })
    setVehicleFound(null); setShowAllTickets(false)
    setVSearch(''); setVResults([])
    setCSearch(''); setCResults([]); setCLastSearch('')
    setOpSelectedName(''); setOpSearch(''); setOpOpen(false)
    setCatSelectedName(''); setCatOpen(false)
    setSubcatSelectedName(''); setSubcatOpen(false)
    setStationSelectedName(''); setStationSearch(''); setStationOpen(false)
    setAttachments([])
  }

  /* ── issue + submit block (shared between auto and manual modes) ── */
  const issueBlock = (
    <div className="form-section">
      <div className="fshdr">Issue Details</div>
      <div className="fg">

        {/* Category */}
        <div className="fgr">
          <label>Issue Category <span className="req">*</span></label>
          {catsError ? (
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <span style={{ fontSize: '11px', color: '#DC2626' }}>Failed to load — </span>
              <button onClick={() => loadCategories(1, false)} style={{ fontSize: '11px', color: '#1B4FD8', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>retry</button>
            </div>
          ) : (
            <div ref={catDropRef} style={{ position: 'relative' }}>
              <div
                onClick={() => setCatOpen(o => !o)}
                style={{
                  border: `1px solid ${catOpen ? '#6366F1' : '#E0E7EF'}`, borderRadius: 8, padding: '9px 12px',
                  cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  background: '#fff', fontSize: 13, color: form.categorySlug ? '#111827' : '#9CA3AF', minHeight: 38,
                }}
              >
                <span>{catsLoading && catList.length === 0 ? 'Loading…' : (form.categorySlug ? catSelectedName : 'Select category…')}</span>
                <span style={{ color: '#9CA3AF', fontSize: 10, marginLeft: 8 }}>▼</span>
              </div>
              {catOpen && (
                <div
                  ref={catListRef}
                  onScroll={handleCatScroll}
                  style={{
                    position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                    background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                    boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                  }}
                >
                  <div
                    onClick={() => { handleCategoryChange(''); setCatSelectedName(''); setCatOpen(false) }}
                    style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                    onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                  >
                    Select category…
                  </div>
                  {catList.map(c => (
                    <div
                      key={c.id || c.slug}
                      onClick={() => { handleCategoryChange(c.slug || c.name); setCatSelectedName(c.name); setCatOpen(false) }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                        background: form.categorySlug === (c.slug || c.name) ? '#EFF6FF' : undefined,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = form.categorySlug === (c.slug || c.name) ? '#DBEAFE' : '#F8FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = form.categorySlug === (c.slug || c.name) ? '#EFF6FF' : '#fff'}
                    >
                      <div style={{ fontSize: 13, color: '#111827' }}>{c.name}</div>
                    </div>
                  ))}
                  {catsLoading && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>
                  )}
                  {!catsLoading && catList.length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No categories found</div>
                  )}
                  {!catsLoading && catHasMore && (
                    <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Sub-category */}
        <div className="fgr">
          <label>Issue Sub Category <span className="req">*</span></label>
          <div ref={subcatDropRef} style={{ position: 'relative' }}>
            <div
              onClick={() => { if (form.categorySlug) setSubcatOpen(o => !o) }}
              style={{
                border: `1px solid ${subcatOpen ? '#6366F1' : '#E0E7EF'}`, borderRadius: 8, padding: '9px 12px',
                cursor: form.categorySlug ? 'pointer' : 'not-allowed', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                background: form.categorySlug ? '#fff' : '#F8FAFC', fontSize: 13,
                color: form.subCategorySlug ? '#111827' : '#9CA3AF', minHeight: 38,
              }}
            >
              <span>
                {!form.categorySlug ? 'Select category first' : subcatsLoading && subcatList.length === 0 ? 'Loading…' : (form.subCategorySlug ? subcatSelectedName : 'Select sub-category…')}
              </span>
              <span style={{ color: '#9CA3AF', fontSize: 10, marginLeft: 8 }}>▼</span>
            </div>
            {subcatOpen && (
              <div
                ref={subcatListRef}
                onScroll={handleSubcatScroll}
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                  boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                }}
              >
                <div
                  onClick={() => { handleSubCategoryChange('', '', ''); setSubcatSelectedName(''); setSubcatOpen(false) }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  Select sub-category…
                </div>
                {subcatList.map(s => (
                  <div
                    key={s.id || s.slug}
                    onClick={() => { handleSubCategoryChange(s.slug || s.name, s.name, s.id); setSubcatSelectedName(s.name); setSubcatOpen(false) }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                      background: form.subCategorySlug === (s.slug || s.name) ? '#EFF6FF' : undefined,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = form.subCategorySlug === (s.slug || s.name) ? '#DBEAFE' : '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = form.subCategorySlug === (s.slug || s.name) ? '#EFF6FF' : '#fff'}
                  >
                    <div style={{ fontSize: 13, color: '#111827' }}>{s.name}</div>
                  </div>
                ))}
                {subcatsLoading && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>
                )}
                {!subcatsLoading && subcatList.length === 0 && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No sub-categories found</div>
                )}
                {!subcatsLoading && subcatHasMore && (
                  <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Region */}
        <div className="fgr">
          <label>Region <span className="req">*</span></label>
          <select className={form.regionId ? 'selected' : ''} value={form.regionId} onChange={e => {
            const r = regionsList.find(x => x.id === e.target.value)
            setForm(f => ({ ...f, regionId: e.target.value, regionSlug: r?.slug || '' }))
          }} disabled={regionsLoading}>
            <option value="">{regionsLoading ? 'Loading…' : 'Select region…'}</option>
            {regionsList.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>

        {/* Coordinator Assigned (read-only, from API) */}
        <div className="fgr">
          <label>Co-ordinator Assigned</label>
          <input
            value={coordinatorLoading ? 'Loading…' : (form.changeCoordinatorId ? '' : (form.coordinatorName || ''))}
            readOnly
            placeholder="Auto-assigned based on category"
            style={{ background: '#F8FAFC', color: '#374151', cursor: 'default' }}
          />
        </div>

        {/* Change Assigned Coordinator */}
        <div className="fgr">
          <label>Change Assigned Coordinator</label>
          <div ref={opDropRef} style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Keep auto-assigned"
                value={opOpen ? opSearch : (opSelectedName || '')}
                onFocus={() => { setOpOpen(true); setOpSearch('') }}
                onChange={e => setOpSearch(e.target.value)}
                autoComplete="off"
                style={{ paddingRight: form.changeCoordinatorId ? '28px' : undefined }}
              />
              {form.changeCoordinatorId && !opOpen && (
                <button
                  type="button"
                  onClick={() => { set('changeCoordinatorId', ''); setOpSelectedName('') }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              )}
            </div>
            {opOpen && (
              <div
                ref={opListRef}
                onScroll={handleOpScroll}
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                  boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                }}
              >
                <div
                  onClick={() => { set('changeCoordinatorId', ''); setOpSelectedName(''); setOpOpen(false) }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  Keep auto-assigned
                </div>
                {opList.map(a => (
                  <div
                    key={a.id}
                    onClick={() => { set('changeCoordinatorId', a.id); setOpSelectedName(a.agent_name); setOpOpen(false) }}
                    style={{
                      padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                      background: form.changeCoordinatorId === a.id ? '#EFF6FF' : undefined,
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = form.changeCoordinatorId === a.id ? '#DBEAFE' : '#F8FAFB'}
                    onMouseLeave={e => e.currentTarget.style.background = form.changeCoordinatorId === a.id ? '#EFF6FF' : '#fff'}
                  >
                    <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{a.agent_name}</div>
                    {a.agent_code && <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{a.agent_code}</div>}
                  </div>
                ))}
                {opSearch.length === 1 && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Type at least 2 characters to search…</div>
                )}
                {opLoading && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>Loading…</div>
                )}
                {!opLoading && opSearch.length !== 1 && opList.length === 0 && (
                  <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No operators found</div>
                )}
                {!opLoading && opHasMore && (
                  <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Type of Ticket */}
        <div className="fgr">
          <label>Type of Ticket <span className="req">*</span></label>
          <select className={form.ticketType ? 'selected' : ''} value={form.ticketType} onChange={e => {
            const val = e.target.value
            if (val === 'self_assign') {
              setForm(f => ({ ...f, ticketType: val, coordinatorId: '', coordinatorName: '', changeCoordinatorId: '' }))
              setOpSelectedName('')
            } else {
              setForm(f => ({ ...f, ticketType: val, disposition: '' }))
            }
          }}>
            <option value="">Select type…</option>
            {TICKET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>

        {/* Previous Ticket — only for Follow Up type */}
        {form.ticketType === 'follow_up' && (
          <div className="fgr">
            <label>Previous Ticket</label>
            <select
              className={form.previousTicketId ? 'selected' : ''}
              value={form.previousTicketId}
              onChange={e => set('previousTicketId', e.target.value)}
              disabled={prevTicketsLoading || !form.phone}
            >
              <option value="">
                {!form.phone ? 'Enter phone number first' : prevTicketsLoading ? 'Loading…' : prevTicketsList.length === 0 ? 'No previous tickets found' : 'Select previous ticket…'}
              </option>
              {prevTicketsList.map(t => (
                <option key={t.id} value={t.id}>{t.ticket_number}</option>
              ))}
            </select>
          </div>
        )}

        {/* Disposition — only for Self Assign */}
        {form.ticketType === 'self_assign' && (
          <div className="fgr">
            <label>Disposition</label>
            <select
              className={form.disposition ? 'selected' : ''}
              value={form.disposition}
              onChange={e => set('disposition', e.target.value)}
              disabled={dispositionLoading}
            >
              <option value="">{dispositionLoading ? 'Loading…' : 'Select disposition…'}</option>
              {dispositionList.map(d => (
                <option key={d.id} value={d.name}>{d.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Priority */}
        <div className="fgr">
          <label>Priority <span className="req">*</span></label>
          <select className={form.priority ? 'selected' : ''} value={form.priority} onChange={e => set('priority', e.target.value)}>
            {PRIORITIES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
        </div>

        {/* Location Type */}
        <div className="fgr">
          <label>Location Type <span className="req">*</span></label>
          <select className={form.locationType ? 'selected' : ''} value={form.locationType} onChange={e => set('locationType', e.target.value)}>
            <option value="">Select location type…</option>
            {LOCATION_TYPES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>

        {/* Incident City — from /api/geographies/geography/cities */}
        <div className="fgr">
          <label>Incident City <span className="req">*</span></label>
          <select
            className={form.city ? 'selected' : ''}
            value={form.city}
            onChange={e => {
              const c = citiesList.find(x => x.id === e.target.value)
              setForm(f => ({ ...f, city: e.target.value, citySlug: c?.slug || '', stationId: '', stationSlug: '' }))
            }}
            disabled={citiesLoading}
          >
            <option value="">{citiesLoading ? 'Loading…' : 'Select city…'}</option>
            {citiesList.map(c => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </div>

        {/* Station ID — always visible */}
        <div className="fgr">
          <label>Station ID <span className="req">*</span></label>
          <div ref={stationDropRef} style={{ position: 'relative' }}>
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder={!form.city ? 'Select city first' : stationsLoading ? 'Loading…' : 'Search station…'}
                value={stationOpen ? stationSearch : (stationSelectedName || '')}
                onFocus={() => { if (form.city) { setStationOpen(true); setStationSearch('') } }}
                onChange={e => setStationSearch(e.target.value)}
                disabled={!form.city}
                autoComplete="off"
                style={{ paddingRight: form.stationId ? '28px' : undefined }}
              />
              {form.stationId && !stationOpen && (
                <button
                  type="button"
                  onClick={() => {
                    setForm(f => ({
                      ...f,
                      stationId: '',
                      stationSlug: '',
                      ...(f.subCategorySlug !== 'battery-stuck-at-vehicle' ? { nearbyStationId: '' } : {})
                    }))
                    setStationSelectedName('')
                    // setStationSearch('')
                  }}
                  style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 0 }}
                >
                  ×
                </button>
              )}
            </div>
            {stationOpen && (
              <div
                ref={stationListRef}
                onScroll={handleStationScroll}
                style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50,
                  background: '#fff', border: '1px solid #E0E7EF', borderRadius: 8,
                  boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: 220, overflowY: 'auto', marginTop: 4,
                }}>
                <div
                  onClick={() => {
                    setForm(f => ({ ...f, stationId: '', stationSlug: '', ...(f.subCategorySlug !== 'battery-stuck-at-vehicle' ? { nearbyStationId: '' } : {}) }))
                    setStationSelectedName('')
                    setStationOpen(false)
                  }}
                  style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F5F9' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  Select station…
                </div>
                {stationsList
                  .filter(s => {
                    if (!stationSearch) return true
                    const q = stationSearch.toLowerCase()
                    return (s.name || '').toLowerCase().includes(q) || (s.station_code || '').toLowerCase().includes(q)
                  })
                  .map((s, i) => (
                    <div
                      key={s.id || i}
                      onClick={() => {
                        const stationName = s.name || s.slug || s.id

                        setForm(f => ({
                          ...f,
                          stationId: s.id || s.slug,
                          stationSlug: s.slug || '',
                          ...(f.subCategorySlug !== 'battery-stuck-at-vehicle'
                            ? { nearbyStationId: s.station_code || '' }
                            : {}),
                        }))

                        setStationSelectedName(stationName)
                        setStationSearch(stationName)
                        setStationOpen(false)
                      }}
                      style={{
                        padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9',
                        background: form.stationId === (s.id || s.slug) ? '#EFF6FF' : undefined,
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = form.stationId === (s.id || s.slug) ? '#DBEAFE' : '#F8FAFB'}
                      onMouseLeave={e => e.currentTarget.style.background = form.stationId === (s.id || s.slug) ? '#EFF6FF' : '#fff'}
                    >
                      <div style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>{s.name || s.slug || s.id}</div>
                      {s.station_code && <div style={{ fontSize: 11, color: '#9CA3AF', fontFamily: 'monospace' }}>{s.station_code}</div>}
                    </div>
                  ))}
                {stationsList.filter(s => {
                  if (!stationSearch) return true
                  const q = stationSearch.toLowerCase()
                  return (s.name || '').toLowerCase().includes(q) || (s.station_code || '').toLowerCase().includes(q)
                }).length === 0 && (
                    <div style={{ padding: '8px 12px', fontSize: 12, color: '#9CA3AF', textAlign: 'center' }}>No stations found</div>
                  )}
                {!stationsLoading && stationsHasMore && (
                  <div style={{ padding: '6px 12px', fontSize: 11, color: '#B0B8C4', textAlign: 'center' }}>↓ Scroll to load more</div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Near by Station ID — only for battery stuck sub-categories */}
        {(['battery-stuck-at-station', 'battery-stuck-in-station', 'battery-stuck-at-vehicle', 'battery-stuck-in-vehicle'].includes(form.subCategorySlug)) && (
          <div className="fgr">
            <label>Near by Station ID</label>
            <input
              placeholder="Nearby station ID"
              value={form.nearbyStationId}
              onChange={e => set('nearbyStationId', e.target.value)}
            />
          </div>
        )}

        {/* Dock ID */}
        <div className="fgr">
          <label>Dock ID</label>
          <input placeholder="Dock ID" value={form.dockId} onChange={e => set('dockId', e.target.value)} />
        </div>



        {/* Incident Area */}
        <div className="fgr">
          <label>Incident Area</label>
          <input placeholder="Area / Locality" value={form.area} onChange={e => set('area', e.target.value)} />
        </div>
      </div>

      {/* Description */}
      <div className="fgr" style={{ marginTop: '10px' }}>
        <label>Description <span className="req">*</span></label>
        <textarea placeholder="Describe the issue as reported by the rider…" value={form.description} onChange={e => set('description', e.target.value)} />
      </div>

      {/* Attachments */}
      <div style={{ marginTop: '12px' }}>
        <label style={{ fontSize: '13px', fontWeight: 500, color: '#374151', display: 'block', marginBottom: '6px' }}>
          Attachments <span style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: 400 }}>(optional)</span>
        </label>
        <input ref={fileInputRef} type="file" multiple style={{ display: 'none' }} onChange={handleFileSelect} />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '7px 14px', border: '1.5px dashed #CBD5E1',
            borderRadius: 7, background: '#F8FAFC', cursor: 'pointer',
            fontSize: 12, color: '#64748B', fontWeight: 500,
          }}
        >
          📎 Attach Files
        </button>
        {attachments.length > 0 && (
          <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {attachments.map((file, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '5px 10px', background: '#F1F5F9', borderRadius: 6,
              }}>
                <span style={{ fontSize: 12, flex: 1, color: '#374151', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  📄 {file.name}
                </span>
                <span style={{ fontSize: 11, color: '#9CA3AF', flexShrink: 0 }}>
                  {file.size < 1024 * 1024
                    ? `${(file.size / 1024).toFixed(0)} KB`
                    : `${(file.size / (1024 * 1024)).toFixed(1)} MB`}
                </span>
                <button
                  type="button"
                  onClick={() => removeAttachment(i)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, padding: 0, lineHeight: 1 }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="action-row" style={{ marginTop: '14px' }}>
        <button className="btn btn-success" style={{ flex: 1, justifyContent: 'center', fontSize: '13px', padding: '9px' }} onClick={() => handleSubmit()} disabled={submitting || resolvingNow}>
          {submitting ? 'Submitting…' : '✓ Submit Ticket'}
        </button>
        <button
          className="btn"
          style={{ flex: 1, justifyContent: 'center', fontSize: '13px', padding: '9px', background: '#7C3AED', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' }}
          onClick={() => handleSubmit('resolve_now')}
          disabled={submitting || resolvingNow}
        >
          {resolvingNow ? 'Resolving…' : '⚡ Resolve Now'}
        </button>
        <button className="btn btn-secondary" style={{ flex: 1, justifyContent: 'center' }} onClick={handleReset} disabled={submitting || resolvingNow}>Clear</button>
      </div>
    </div>
  )

  return (
    <div>
      {/* ── Draft restore modal ── */}
      {draftModalOpen && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 10000,
          background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: '#fff', borderRadius: 12, padding: '28px 32px', width: 400, maxWidth: '90vw',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            <div style={{ fontSize: 20, marginBottom: 8 }}>📋</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', marginBottom: 6 }}>
              Resume previous ticket?
            </div>
            <div style={{ fontSize: 13, color: '#6B7280', marginBottom: 24, lineHeight: 1.5 }}>
              You have an unsaved ticket form from a previous session. Do you want to continue where you left off?
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={handleRestoreDraft}
                style={{ flex: 1, padding: '10px 0', background: '#0090D9', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Proceed with previous
              </button>
              <button
                onClick={handleDiscardDraft}
                style={{ flex: 1, padding: '10px 0', background: '#F1F5F9', color: '#374151', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer' }}
              >
                Create new
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Toast notifications ── */}
      {toasts.length > 0 && (
        <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 9999, display: 'flex', flexDirection: 'column', gap: 8, pointerEvents: 'none' }}>
          {toasts.map(t => (
            <div key={t.id} style={{
              pointerEvents: 'all',
              minWidth: 300, maxWidth: 400,
              background: t.ok ? '#ECFDF5' : '#FFF1F1',
              border: `1px solid ${t.ok ? '#6EE7B7' : '#FECACA'}`,
              borderLeft: `4px solid ${t.ok ? '#10B981' : '#EF4444'}`,
              borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,.13)',
              padding: '12px 14px',
              display: 'flex', alignItems: 'flex-start', gap: 10,
              animation: 'slideInRight .2s ease',
            }}>
              <div style={{ fontSize: 18, lineHeight: 1, marginTop: 1, flexShrink: 0 }}>
                {t.ok ? '✅' : '❌'}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: t.ok ? '#065F46' : '#991B1B', lineHeight: 1.4 }}>
                  {t.ok ? 'Ticket Created' : 'Failed to Create Ticket'}
                </div>
                <div style={{ fontSize: 12, color: t.ok ? '#047857' : '#DC2626', marginTop: 3, lineHeight: 1.4 }}>
                  {t.msg}
                </div>
                {t.withViewTickets && (
                  <button
                    onClick={() => {
                      dismissToast(t.id)
                      if (createdTicket?.ticket_id && setSelectedTicket) {
                        setSelectedTicket(createdTicket)
                        setCurrentPage?.('ticket-detail')
                      } else {
                        setCurrentPage?.('tickets')
                      }
                    }}
                    style={{ marginTop: 8, background: '#0090D9', color: '#fff', border: 'none', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    View Ticket →
                  </button>
                )}
              </div>
              <button
                onClick={() => dismissToast(t.id)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#9CA3AF', fontSize: 18, lineHeight: 1, padding: 0, flexShrink: 0 }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Call-linked banner — shown only when opened automatically from a call */}
      {fromCall && (() => {
        let ticketNumber = urlParams.get('ticket_number') || ''
        if (!ticketNumber) {
          try { ticketNumber = JSON.parse(localStorage.getItem('ozonetel_auto_ticket') || '{}')?.ticket_number || '' } catch { }
        }
        return (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: '#EFF6FF', border: '1px solid #BFDBFE',
            borderRadius: 8, padding: '8px 14px', marginBottom: 14,
          }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3B82F6', flexShrink: 0, animation: 'pulse 1.5s infinite' }} />
            <div style={{ fontSize: 12, color: '#1E40AF' }}>
              <strong>Auto-opened from active call</strong>
              {ticketNumber && (
                <span style={{ marginLeft: 8, color: '#3B82F6', fontFamily: 'monospace' }}>Ticket # {ticketNumber}</span>
              )}
            </div>
          </div>
        )
      })()}

      {/* Breadcrumb */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 12, fontSize: 13, color: '#6B7280' }}>
        <button
          type="button"
          onClick={() => setCurrentPage?.('tickets')}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: 6, color: '#6B7280', fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}
          onMouseEnter={e => e.currentTarget.style.background = '#F1F5F9'}
          onMouseLeave={e => e.currentTarget.style.background = 'none'}
        >
          ← Tickets
        </button>
        <span>/</span>
        <span style={{ color: '#111827', fontWeight: 500 }}>Create Ticket</span>
      </div>

      {/* ══════════ VEHICLE DETAILS ══════════ */}
      <div className="form-section">
        <div className="fshdr">Vehicle Details</div>
        <div ref={vDropRef} style={{ position: 'relative', marginBottom: 16 }}>
          <div className="search-row">
            <input
              type="text"
              placeholder="Search by VIN, Reg No."
              value={vSearch}
              onChange={e => { setVSearch(e.target.value); searchVehicles(e.target.value) }}
              onKeyDown={e => e.key === 'Enter' && searchVehicles(vSearch)}
              autoComplete="off"
            />
            <button className="btn btn-primary" onClick={() => searchVehicles(vSearch)} disabled={vSearching}>
              {vSearching ? '…' : 'Search'}
            </button>
          </div>

          {!vSearching && vResults.length === 0 && vLastSearch && !vehicleFound && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: '4px', padding: '12px 14px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
              No results found for &ldquo;{vLastSearch}&rdquo;
            </div>
          )}

          {vResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: '280px', overflowY: 'auto', marginTop: '4px' }}>
              {vResults.map((v, idx) => {
                const vd = v.vehicle_details || {}
                const cd = v.customer_details || {}
                const primaryLabel = vd.registration_number || vd.vin || cd.name || cd.phone || '—'
                return (
                  <div key={idx} onClick={() => selectVehicle(v)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }} onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 600, color: '#1A1F37', fontFamily: 'monospace' }}>🚗 {primaryLabel}</span>
                      {vd.model && <span style={{ fontSize: '11px', color: '#5A6672' }}>{vd.model}</span>}
                      {vd.state && <span style={{ marginLeft: 'auto', fontSize: '10px', fontWeight: 600, padding: '2px 8px', borderRadius: '10px', background: vd.state === 'active' ? '#ECFDF5' : '#FEF2F2', color: vd.state === 'active' ? '#047857' : '#DC2626' }}>{vd.state === 'active' ? '● Active' : vd.state}</span>}
                    </div>
                    {(vd.vin || vd.registration_number) && <div style={{ fontSize: '11px', color: '#9AA5B1', fontFamily: 'monospace', marginTop: '2px' }}>{[vd.vin && `VIN: ${vd.vin}`, vd.registration_number && `Reg: ${vd.registration_number}`].filter(Boolean).join('  ·  ')}</div>}
                    {(cd.name || cd.phone) && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>👤 {[cd.name, cd.phone].filter(Boolean).join(' · ')}</div>}
                    {vd.number_of_open_tickets > 0 && <div style={{ fontSize: '10px', color: '#F59E0B', marginTop: '2px', fontWeight: 600 }}>{vd.number_of_open_tickets} open ticket(s)</div>}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        {vehicleFound && (() => {
          const vd = vehicleFound.vehicle_details || {}
          const stateVal = vd.vehicle_state || vd.state || ''
          const isActive = stateVal === 'active'
          const tickets = vd.previous_ticket_numbers || []
          const visibleTickets = showAllTickets ? tickets : tickets.slice(0, 2)
          return (
            <div style={{ marginBottom: 12, background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, padding: '9px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: tickets.length ? 5 : 0 }}>
                  <span style={{ fontSize: 12, fontWeight: 700, color: '#065F46', fontFamily: 'monospace' }}>
                    {vd.registration_number || vd.vin_number || vd.vin || '—'}
                  </span>
                  <span style={{ background: isActive ? '#DCFCE7' : '#FEE2E2', color: isActive ? '#15803D' : '#DC2626', fontSize: 10, fontWeight: 600, padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                    ● {isActive ? 'Active' : (stateVal || 'Unknown')}
                  </span>
                  {vd.number_of_open_tickets > 0 && (
                    <span style={{ background: '#FEF3C7', color: '#92400E', fontSize: 10, fontWeight: 700, padding: '1px 7px', borderRadius: 10, flexShrink: 0 }}>
                      {vd.number_of_open_tickets} open
                    </span>
                  )}
                </div>
                {tickets.length > 0 && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 10, color: '#6B7280', flexShrink: 0 }}>Prev tickets:</span>
                    {visibleTickets.map((t, i) => (
                      <span key={i} style={{ fontSize: 10, fontFamily: 'monospace', color: '#374151', background: '#fff', border: '1px solid #D1FAE5', borderRadius: 4, padding: '1px 6px' }}>{t}</span>
                    ))}
                    {tickets.length > 2 && (
                      <button onClick={() => setShowAllTickets(v => !v)} style={{ fontSize: 10, color: '#0090D9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, padding: 0 }}>
                        {showAllTickets ? 'see less ↑' : `+${tickets.length - 2} more ↓`}
                      </button>
                    )}
                  </div>
                )}
              </div>
              <button
                onClick={() => { const id = vehicleFound?.vehicle_details?.id; localStorage.setItem('crm_vehicle_360_data', JSON.stringify({ ...vehicleFound, vehicle_id: id, id })); const url = new URL(window.location.href); url.search = ''; url.searchParams.set('supportPage', 'vehicle-360'); url.searchParams.set('nodialer', '1'); window.open(url.toString(), '_blank', 'noopener') }}
                style={{ background: '#fff', border: '1px solid #BBF7D0', borderRadius: 6, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#065F46', flexShrink: 0 }}
              >
                Vehicle 360 ↗
              </button>
            </div>
          )
        })()}

        <div className="fg">
          <div className="fgr">
            <label>Vehicle VIN No <span className="req">*</span></label>
            <input placeholder="Vehicle VIN No." value={form.vin} onChange={e => set('vin', e.target.value)} />
          </div>
          <div className="fgr">
            <label>Registration No. <span className="req">*</span></label>
            <input placeholder="DL01AB1234" value={form.registrationNumber} onChange={e => set('registrationNumber', e.target.value)} />
          </div>
          <div className="fgr">
            <label>Battery ID</label>
            <input placeholder="Battery ID" value={form.batteryId} onChange={e => set('batteryId', e.target.value)} />
          </div>
          <div className="fgr">
            <label>Type of Vehicle</label>
            <select value={form.vehicleType} onChange={e => set('vehicleType', e.target.value)}>
              <option value="">Select type</option>
              <option value="E-Auto">E-Auto</option>
              <option value="E-Loader">E-Loader</option>
              <option value="E-Rickshaw">E-Rickshaw</option>
              <option value="2 Wheeler">2 Wheeler</option>
              <option value="Other">Other</option>
            </select>
          </div>
          <div className="fgr">
            <label>OEM Brand Name</label>
            <select className={form.oemBrandName ? 'selected' : ''} value={form.oemBrandName} onChange={e => set('oemBrandName', e.target.value)}>
              <option value="">{oemList.length === 0 ? 'Loading…' : 'Select brand'}</option>
              {oemList.map(b => <option key={b.id} value={b.name}>{b.name}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>Service Provider</label>
            <select className={form.serviceProvider ? 'selected' : ''} value={form.serviceProvider} onChange={e => set('serviceProvider', e.target.value)}>
              <option value="">{serviceProviderList.length === 0 ? 'Loading…' : 'Select provider'}</option>
              {serviceProviderList.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>CNC Error Code</label>
            <select className={form.cncErrorCode ? 'selected' : ''} value={form.cncErrorCode} onChange={e => set('cncErrorCode', e.target.value)}>
              <option value="">{cncErrorList.length === 0 ? 'Loading…' : 'Select error code'}</option>
              {cncErrorList.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>CNC Transaction ID <span className="req">*</span></label>
            <input placeholder="CNC-XXXX" value={form.cncTransactionId} onChange={e => set('cncTransactionId', e.target.value)} />
            <span style={{ fontSize: 10, color: '#9CA3AF' }}>If no ID available, write NA</span>
          </div>
        </div>
      </div>

      {/* ══════════ CUSTOMER DETAILS ══════════ */}
      <div className="form-section">
        <div className="fshdr">Customer Details</div>
        <div ref={cDropRef} style={{ position: 'relative', marginBottom: 16 }}>
          <div className="search-row">
            <input
              type="text"
              placeholder="Search by name or phone…"
              value={cSearch}
              onChange={e => { setCSearch(e.target.value); searchCustomers(e.target.value) }}
              onKeyDown={e => e.key === 'Enter' && searchCustomers(cSearch)}
              autoComplete="off"
            />
            <button className="btn btn-primary" onClick={() => searchCustomers(cSearch)} disabled={cSearching}>
              {cSearching ? '…' : 'Search'}
            </button>
          </div>
          {!cSearching && cResults.length === 0 && cLastSearch && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', marginTop: '4px', padding: '12px 14px', fontSize: 13, color: '#9CA3AF', textAlign: 'center' }}>
              No results found for &ldquo;{cLastSearch}&rdquo;
            </div>
          )}

          {cResults.length > 0 && (
            <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, background: '#fff', border: '1px solid #E0E7EF', borderRadius: '8px', boxShadow: '0 6px 18px rgba(0,0,0,.12)', maxHeight: '280px', overflowY: 'auto', marginTop: '4px' }}>
              {cResults.map((r, idx) => {
                const cd = r.customer_details || r
                const vd = r.vehicle_details || {}
                return (
                  <div key={idx} onClick={() => selectCustomerResult(r)} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #F1F5F9' }} onMouseEnter={e => e.currentTarget.style.background = '#F8FAFB'} onMouseLeave={e => e.currentTarget.style.background = '#fff'}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: '#1A1F37' }}>👤 {cd.name || '—'}</div>
                    {cd.phone && <div style={{ fontSize: '11px', color: '#6B7280', marginTop: '2px' }}>{cd.phone}</div>}
                    {(vd.vin || vd.registration_number) && <div style={{ fontSize: '11px', color: '#9AA5B1', fontFamily: 'monospace', marginTop: '2px' }}>🚗 {[vd.vin, vd.registration_number].filter(Boolean).join(' · ')}</div>}
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <div className="fg">
          <div className="fgr">
            <label>Rider Name <span className="req">*</span></label>
            <input placeholder="Full name" value={form.riderName} onChange={e => set('riderName', e.target.value)} />
          </div>
          <div className="fgr">
            <label>Rider Mobile <span className="req">*</span></label>
            <input placeholder="+91 98765 43210" value={form.phone} onChange={e => set('phone', e.target.value)} />
          </div>
          <div className="fgr">
            <label>Rider Email</label>
            <input type="email" placeholder="rider@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
          </div>
          <div className="fgr">
            <label>Rider Preferred Channel</label>
            <select className={form.preferredChannel ? 'selected' : ''} value={form.preferredChannel} onChange={e => set('preferredChannel', e.target.value)}>
              <option value="">Select channel…</option>
              {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>Customer Type (Account) <span className="req">*</span></label>
            <select className={form.customerType ? 'selected' : ''} value={form.customerType} onChange={e => set('customerType', e.target.value)}>
              <option value="">Select type…</option>
              {CUSTOMER_TYPES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>Source Channel <span className="req">*</span></label>
            <select className={form.channel ? 'selected' : ''} value={form.channel} onChange={e => set('channel', e.target.value)}>
              <option value="">Select source…</option>
              {SOURCE_CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>Language</label>
            <select className={form.language ? 'selected' : ''} value={form.language} onChange={e => set('language', e.target.value)}>
              {LANGUAGES.map(l => <option key={l}>{l}</option>)}
            </select>
          </div>
          <div className="fgr">
            <label>Rider Experience <span className="req">*</span></label>
            <select className={form.experience ? 'selected' : ''} value={form.experience} onChange={e => set('experience', e.target.value)}>
              <option value="">Select experience…</option>
              {EXPERIENCES.map(ex => <option key={ex.value} value={ex.value}>{ex.label}</option>)}
            </select>
          </div>
        </div>
      </div>

      {issueBlock}
    </div>
  )
}

export default CreateTicketPage
